/**
 * Connector hooks — Wave 2 finish.
 *
 * Wraps /api/v1/library/connectors endpoints in TanStack Query hooks.
 * Mirrors the pattern used by useIntegrations / useNotifications.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

import type {
  LibraryConnectorSyncSchedule,
  LibraryConnectorPauseReason,
} from '@argusai/shared/schemas/library-connector.schema';

export type ConnectorKind = 'onedrive' | 'sharepoint' | 'github' | 'webhook';
/** The schedule alphabet — declared once, in @argusai/shared (LB6b-T0 / T2). */
export type SyncSchedule = LibraryConnectorSyncSchedule;
/** Why the system paused a source (L6b-08) — never a person's "Sync paused" (`syncEnabled`). */
export type ConnectorPauseReason = LibraryConnectorPauseReason;

export interface ConnectorSummary {
  id: string;
  organizationId: string;
  connectorKind: ConnectorKind;
  displayName: string;
  syncSchedule: SyncSchedule;
  syncEnabled: boolean;
  isActive: boolean;
  hasCredentials: boolean;
  /**
   * Decision 35 — the classification the source's synced documents take
   * (the shared vocabulary). Shown and edited only under the
   * classification-controls rule (use-classification-policy.ts).
   */
  defaultSensitivity: string;
  defaultExportControl: string;
  /** 'credentials': sign-in failed — reconnect; 'failures': failed syncs in a row; null: not paused by the system. */
  pausedReason: ConnectorPauseReason | null;
  pausedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  lastSyncDocCount: number;
  lastSyncDurationMs: number | null;
  targetFolderId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectorRunSummary {
  id: string;
  runType: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  docsAdded: number;
  docsUpdated: number;
  docsDeleted: number;
  docsSkippedDedup: number;
  errorMessage: string | null;
}

export interface ConnectorDetail extends ConnectorSummary {
  config: Record<string, unknown>;
  recentRuns: ConnectorRunSummary[];
}

export interface ConnectorListResponse {
  data: ConnectorSummary[];
  total: number;
  page: number;
  pageSize: number;
}

const KEY = ['library', 'connectors'] as const;

/**
 * The connector list (GET /library/connectors → read:library.documents).
 * `enabled` is the caller's answer to that key: a person who may not read the
 * list makes no call for it (LB-6 run 1 live proof — a Viewer's Access
 * Required page still fired two 403s).
 */
export function useConnectors(
  filters?: {
    kind?: ConnectorKind;
    status?: 'active' | 'paused' | 'error' | 'deleted';
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...KEY, 'list', filters ?? {}] as const,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.kind) params.set('kind', filters.kind);
      if (filters?.status) params.set('status', filters.status);
      return apiClient.get<ConnectorListResponse>(
        `/library/connectors${params.toString() ? `?${params}` : ''}`,
      );
    },
    enabled: options.enabled ?? true,
  });
}

export function useConnectorDetail(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', id] as const,
    queryFn: async () => apiClient.get<ConnectorDetail>(`/library/connectors/${id}`),
    enabled: !!id,
  });
}

export function useConnectorRuns(id: string | null, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: [...KEY, 'runs', id, page, pageSize] as const,
    queryFn: async () =>
      apiClient.get<{
        data: ConnectorRunSummary[];
        total: number;
        page: number;
        pageSize: number;
      }>(`/library/connectors/${id}/runs?page=${page}&pageSize=${pageSize}`),
    enabled: !!id,
  });
}

export function useConnectorDocs(id: string | null, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: [...KEY, 'docs', id, page, pageSize] as const,
    queryFn: async () =>
      apiClient.get<{
        data: {
          id: string;
          externalSourcePath: string;
          externalSourceId: string | null;
          contentHash: string;
          documentId: string | null;
          lastSeenAt: string;
          firstSeenAt: string;
          /** A person deleted this item's document; a sync does not bring it back (OQ-4). */
          removed: { at: string; byName: string | null } | null;
        }[];
        total: number;
        page: number;
        pageSize: number;
      }>(`/library/connectors/${id}/docs?page=${page}&pageSize=${pageSize}`),
    enabled: !!id,
  });
}

export interface CreateConnectorInput {
  kind: ConnectorKind;
  displayName: string;
  syncSchedule?: SyncSchedule;
  syncEnabled?: boolean;
  config?: Record<string, unknown>;
  targetFolderId?: string | null;
  /** Sent ONLY for a control the tenant's frameworks show (decision 28) — never a default for a hidden one. */
  defaultSensitivity?: string;
  defaultExportControl?: string;
}

/**
 * The 201 of a create. Decision 62 — a webhook source's `signingSecret` is
 * here, ONCE, and in no other body. The caller keeps it in component state
 * only (never the query cache, storage, the URL, console or a toast) and drops
 * it when the show-once dialog closes.
 */
export type CreatedConnector = ConnectorSummary & { signingSecret?: string };

/**
 * `gcTime: 0` on the two mutations whose result carries a signing secret: the
 * mutation (and its `data`) leaves the TanStack cache as soon as the screen
 * that ran it unmounts — the secret lives no longer than the screen does.
 */
const SECRET_BEARING_MUTATION_GC_MS = 0;

export function useCreateConnector() {
  const qc = useQueryClient();
  return useMutation({
    gcTime: SECRET_BEARING_MUTATION_GC_MS,
    mutationFn: async (input: CreateConnectorInput) =>
      apiClient.post<CreatedConnector>('/library/connectors', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY] });
    },
  });
}

export interface UpdateConnectorInput {
  id: string;
  displayName?: string;
  syncSchedule?: SyncSchedule;
  syncEnabled?: boolean;
  config?: Record<string, unknown>;
  targetFolderId?: string | null;
  defaultSensitivity?: string;
  defaultExportControl?: string;
}

export function useUpdateConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateConnectorInput) => {
      const { id, ...patch } = input;
      return apiClient.patch<ConnectorSummary>(`/library/connectors/${id}`, patch);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.id] });
    },
  });
}

export function useDeleteConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      apiClient.delete<{ success: boolean }>(`/library/connectors/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY] });
    },
  });
}

export function useSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      apiClient.post<{
        runId: string;
        status: string;
        docsAdded: number;
        docsUpdated: number;
        docsDeleted: number;
        docsSkippedDedup: number;
      }>(`/library/connectors/${id}/sync-now`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [...KEY] });
      qc.invalidateQueries({ queryKey: [...KEY, 'runs', id] });
    },
  });
}

/** The 200 of a rotate: the new secret, shown once (decision 62). */
export interface RotatedSigningSecret {
  id: string;
  signingSecret: string;
  rotatedAt: string;
}

/**
 * Rotate a webhook source's signing secret (POST
 * /library/connectors/:id/rotate-secret → write:library). The new secret is
 * returned to the caller only; the previous one stops working at once.
 */
export function useRotateSigningSecret() {
  const qc = useQueryClient();
  return useMutation({
    gcTime: SECRET_BEARING_MUTATION_GC_MS,
    mutationFn: async (id: string) =>
      apiClient.post<RotatedSigningSecret>(`/library/connectors/${id}/rotate-secret`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

/**
 * The ONE place the web spells a webhook source's delivery URL (POST
 * /api/v1/library/connectors/:id/webhook) — the connect wizard and the
 * source's Settings card both read it here.
 */
export function webhookUrlFor(id: string): string {
  // `globalThis.location` — a server-side render (the per-role tests) has no
  // window; the path is still the one the receiver answers.
  const origin = globalThis.location?.origin ?? '';
  return `${origin}/api/v1/library/connectors/${id}/webhook`;
}

export function useStartOAuth() {
  return useMutation({
    mutationFn: async (id: string) =>
      apiClient.post<{ authUrl: string; state: string }>(
        `/library/connectors/${id}/oauth-start`,
      ),
  });
}

export function useGitHubRepos(connectorId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'github-repos', connectorId] as const,
    queryFn: async () =>
      apiClient.get<{
        data: { owner: string; name: string; defaultBranch: string; private: boolean }[];
      }>(`/library/connectors/${connectorId}/github/repos`),
    enabled: !!connectorId,
  });
}
